import { styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "components/Typography";
import Div from "components/primitives/Div";
import type { UpsellFeature } from "types/upsell";
import UpsellRequestAccessButton from "../UpsellRequestAccessButton";

/**
 * The mint prompt bar across the top of an upsell landing page.
 *
 * This lives inside the page rather than in the global `components/Banners`
 * stack — in the design it sits to the right of the sidebar and above the page
 * content, and it should only ever appear on the upsell pages.
 */
const Root = styled(Div)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  /** Wraps rather than squeezing the CTA when a translation runs long. */
  flexWrap: "wrap",
  gap: theme.spacing(2),
  minHeight: "60px",
  padding: theme.spacing(1.5, 3),
  backgroundColor: theme.palette.secondary1[60],
  /**
   * Stays in view while the coach scrolls the page. The scroll container is
   * `PageContainer`'s `Main` (`overflowY: auto`), which is this element's
   * nearest scrollable ancestor, so `top: 0` pins it just below the app chrome.
   */
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar,
}));

const Prompt = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.common.black,
}));

interface UpsellTopBannerProps {
  feature: UpsellFeature;
  /** Translation key for the prompt shown beside the CTA. */
  prompt: string;
}

function UpsellTopBanner(props: UpsellTopBannerProps) {
  const { feature, prompt } = props;

  const { t } = useTranslation();

  return (
    <Root>
      <Prompt variant="TextM">{t(prompt)}</Prompt>
      <UpsellRequestAccessButton feature={feature} tone="dark" size="small" origin="topBanner" />
    </Root>
  );
}

export default UpsellTopBanner;
